import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { 
  ArrowLeft, 
  Clock, 
  Target, 
  CheckCircle, 
  XCircle, 
  Trophy, 
  RefreshCw,
  Star,
  Flame,
  TrendingUp,
  BookOpen,
  Play,
  Pause,
  RotateCcw,
  Volume2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RitualsGame = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [gameState, setGameState] = useState('menu'); // menu, learning, practice, testing, completed
  const [sessionId, setSessionId] = useState(null);
  const [currentRitualIndex, setCurrentRitualIndex] = useState(0);
  const [rituals, setRituals] = useState([]);
  const [practiceMode, setPracticeMode] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [timeLeft, setTimeLeft] = useState(60);
  const [gameResults, setGameResults] = useState(null);
  const [difficulty, setDifficulty] = useState('beginner');
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [practiceTimer, setPracticeTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  const difficulties = [
    {
      value: 'beginner',
      label: 'Principiante',
      description: 'Aprende moyubas básicas y rituales fundamentales',
      icon: '🕯️',
      color: 'from-amber-500 to-orange-600'
    },
    {
      value: 'intermediate', 
      label: 'Intermedio',
      description: 'Domina ceremonias complejas y cantos rituales',
      icon: '🔥',
      color: 'from-red-500 to-pink-600'
    },
    {
      value: 'advanced',
      label: 'Avanzado',
      description: 'Ceremonias completas y rituales de iniciación',
      icon: '⚡',
      color: 'from-purple-500 to-violet-600'
    },
    {
      value: 'expert',
      label: 'Experto',
      description: 'Rituales de alta jerarquía y ceremonias sagradas',
      icon: '👑',
      color: 'from-yellow-500 to-amber-600'
    }
  ];

  // Sample ritual data with step-by-step instructions
  const sampleRituals = [
    {
      name: "Moyuba Básica",
      description: "Saludo fundamental a los ancestros y Orishas",
      category: "Moyubas",
      difficulty: "beginner",
      steps: [
        { id: 1, instruction: "Toca el suelo con la mano derecha", duration: 3, type: "gesture" },
        { id: 2, instruction: "Di: 'Ibae bayen tonu' (Saludo a los ancestros)", duration: 5, type: "speech" },
        { id: 3, instruction: "Toca tu frente con ambas manos", duration: 2, type: "gesture" },
        { id: 4, instruction: "Di: 'Eleggua mo juba' (Saludo a Eleggua)", duration: 4, type: "speech" },
        { id: 5, instruction: "Aplaude tres veces", duration: 3, type: "sound" }
      ],
      audio_url: "ritual_moyuba.mp3",
      total_duration: 17
    },
    {
      name: "Preparación de Velas",
      description: "Ritual para encender velas a los Orishas",
      category: "Luminarias",
      difficulty: "beginner",
      steps: [
        { id: 1, instruction: "Limpia la vela con un paño blanco", duration: 10, type: "preparation" },
        { id: 2, instruction: "Sostén la vela entre ambas manos", duration: 5, type: "gesture" },
        { id: 3, instruction: "Concentra tu intención y pide permiso", duration: 15, type: "meditation" },
        { id: 4, instruction: "Enciende la vela desde la base hacia la punta", duration: 5, type: "action" },
        { id: 5, instruction: "Di una oración al Orisha correspondiente", duration: 20, type: "prayer" }
      ],
      audio_url: "ritual_velas.mp3",
      total_duration: 55
    },
    {
      name: "Saludo a los Cuatro Vientos",
      description: "Ceremonia para honrar las direcciones cardinales",
      category: "Ceremonias",
      difficulty: "intermediate",
      steps: [
        { id: 1, instruction: "Mírate hacia el Este y saluda al amanecer", duration: 8, type: "orientation" },
        { id: 2, instruction: "Gírate al Sur, saluda al mediodía", duration: 8, type: "orientation" },
        { id: 3, instruction: "Mírate al Oeste, saluda al atardecer", duration: 8, type: "orientation" },
        { id: 4, instruction: "Gírate al Norte, saluda a la medianoche", duration: 8, type: "orientation" },
        { id: 5, instruction: "Regresa al centro y agradece", duration: 10, type: "gratitude" }
      ],
      audio_url: "ritual_vientos.mp3",
      total_duration: 42
    }
  ];

  // Timer effects
  useEffect(() => {
    if (gameState === 'testing' && timeLeft > 0 && !selectedAnswer) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !selectedAnswer) {
      handleAnswerSubmit(''); 
    }
  }, [gameState, timeLeft, selectedAnswer]);

  useEffect(() => {
    if (practiceMode && isTimerRunning) {
      const timer = setTimeout(() => setPracticeTimer(practiceTimer + 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [practiceMode, isTimerRunning, practiceTimer]);

  const startLearning = async (selectedDifficulty) => {
    setLoading(true);
    try {
      // For now, use sample data. In production, this would call the backend
      const filteredRituals = sampleRituals.filter(r => r.difficulty === selectedDifficulty);
      setRituals(sampleRituals); // Show all rituals for learning
      setDifficulty(selectedDifficulty);
      setGameState('learning');
      setCurrentRitualIndex(0);
      
      toast.success('¡Comienza tu aprendizaje de rituales!');
    } catch (error) {
      console.error('Error starting game:', error);
      toast.error('Error al iniciar el aprendizaje');
    } finally {
      setLoading(false);
    }
  };

  const startPractice = (ritual) => {
    setPracticeMode(true);
    setCurrentStep(0);
    setCompletedSteps([]);
    setPracticeTimer(0);
    setIsTimerRunning(true);
    setGameState('practice');
    toast.success(`Comenzando práctica: ${ritual.name}`);
  };

  const completeStep = () => {
    const ritual = rituals[currentRitualIndex];
    if (currentStep < ritual.steps.length - 1) {
      setCompletedSteps([...completedSteps, currentStep]);
      setCurrentStep(currentStep + 1);
      toast.success('¡Paso completado!');
    } else {
      setCompletedSteps([...completedSteps, currentStep]);
      setIsTimerRunning(false);
      toast.success('¡Ritual completado exitosamente!');
      setTimeout(() => {
        setGameState('learning');
        setPracticeMode(false);
      }, 2000);
    }
  };

  const resetPractice = () => {
    setCurrentStep(0);
    setCompletedSteps([]);
    setPracticeTimer(0);
    setIsTimerRunning(true);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStepTypeIcon = (type) => {
    switch (type) {
      case 'gesture': return '🤲';
      case 'speech': return '🗣️';
      case 'sound': return '👏';
      case 'preparation': return '🧹';
      case 'meditation': return '🧘';
      case 'prayer': return '🙏';
      case 'action': return '⚡';
      case 'orientation': return '🧭';
      case 'gratitude': return '❤️';
      default: return '📝';
    }
  };

  const resetGame = () => {
    setGameState('menu');
    setSessionId(null);
    setRituals([]);
    setCurrentRitualIndex(0);
    setCurrentStep(0);
    setCompletedSteps([]);
    setScore({ correct: 0, total: 0 });
    setTimeLeft(60);
    setSelectedAnswer('');
    setGameResults(null);
    setShowResult(false);
    setLastAnswerCorrect(null);
    setPracticeMode(false);
    setPracticeTimer(0);
    setIsTimerRunning(false);
  };

  const currentRitual = rituals[currentRitualIndex];

  // Menu Screen
  if (gameState === 'menu') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="container mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link 
              to="/dashboard"
              className="p-2 bg-white rounded-xl shadow-lg text-amber-600 hover:text-amber-800 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-amber-800">Rituales Sagrados</h1>
              <p className="text-amber-600">Aprende moyubas, ceremonias y rituales con práctica guiada</p>
            </div>
          </div>

          {/* Difficulty Selection */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-cinzel text-2xl font-bold text-amber-800 mb-4">Selecciona tu Nivel de Práctica</h2>
              <p className="text-amber-700 max-w-2xl mx-auto">
                Aprende moyubas, ceremonias y rituales sagrados con práctica guiada 
                paso a paso para dominar las tradiciones ancestrales.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {difficulties.map((diff, index) => (
                <div 
                  key={index} 
                  className={`game-card bg-gradient-to-br ${diff.color} p-8 rounded-2xl text-white cursor-pointer relative overflow-hidden`}
                  onClick={() => startLearning(diff.value)}
                >
                  <div className="text-6xl mb-4 text-center">{diff.icon}</div>
                  <h3 className="font-cinzel text-2xl font-bold mb-3 text-center">{diff.label}</h3>
                  <p className="text-white text-opacity-90 text-center leading-relaxed mb-6">
                    {diff.description}
                  </p>
                  
                  <div className="text-center">
                    <button 
                      className="bg-white text-amber-800 px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-800"></div>
                      ) : (
                        <>
                          <Flame className="w-5 h-5" />
                          Comenzar Práctica
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Learning Screen - Ritual Overview
  if (gameState === 'learning') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-900 via-orange-800 to-red-700">
        <div className="container mx-auto px-6 py-8">
          {/* Learning Header */}
          <div className="flex items-center justify-between mb-8 text-white">
            <div className="flex items-center gap-4">
              <button 
                onClick={resetGame}
                className="p-2 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-cinzel text-2xl font-bold">Rituales Sagrados de Ifá</h1>
                <p className="text-orange-200 capitalize">{difficulty} • Aprendizaje Experiencial</p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold">{currentRitualIndex + 1}/{rituals.length}</div>
              <div className="text-orange-200 text-sm">Rituales</div>
            </div>
          </div>

          {/* Ritual Selection Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {rituals.map((ritual, index) => (
              <div 
                key={index}
                className={`bg-white rounded-2xl p-6 shadow-lg cursor-pointer transition-all duration-300 hover:scale-105 ${
                  currentRitualIndex === index ? 'ring-4 ring-yellow-400' : ''
                }`}
                onClick={() => setCurrentRitualIndex(index)}
              >
                <div className="text-4xl mb-4 text-center">
                  {ritual.category === 'Moyubas' ? '🙏' : 
                   ritual.category === 'Luminarias' ? '🕯️' : 
                   ritual.category === 'Ceremonias' ? '⚡' : '🔮'}
                </div>
                <h3 className="font-cinzel text-xl font-bold text-amber-800 mb-2">{ritual.name}</h3>
                <p className="text-amber-600 text-sm mb-4">{ritual.description}</p>
                
                <div className="flex items-center justify-between text-sm text-amber-700">
                  <span className="bg-amber-100 px-3 py-1 rounded-full">{ritual.category}</span>
                  <span>{ritual.steps.length} pasos</span>
                </div>
                
                <div className="mt-4">
                  <div className="text-xs text-amber-600 mb-2">Duración: {formatTime(ritual.total_duration)}</div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentRitualIndex(index);
                      startPractice(ritual);
                    }}
                    className="w-full bg-amber-500 text-white py-2 rounded-lg font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Play className="w-4 h-4" />
                    Practicar
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Current Ritual Details */}
          {currentRitual && (
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="text-center mb-8">
                <h2 className="font-cinzel text-3xl font-bold text-amber-800 mb-4">
                  {currentRitual.name}
                </h2>
                <p className="text-amber-600 text-lg">{currentRitual.description}</p>
                <div className="flex justify-center gap-4 mt-4">
                  <span className="bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-semibold">
                    {currentRitual.category}
                  </span>
                  <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold">
                    {currentRitual.steps.length} pasos • {formatTime(currentRitual.total_duration)}
                  </span>
                </div>
              </div>

              {/* Steps Preview */}
              <div className="mb-8">
                <h3 className="font-bold text-lg text-amber-800 mb-4">Pasos del Ritual</h3>
                <div className="space-y-3">
                  {currentRitual.steps.map((step, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl">
                      <div className="flex-shrink-0 w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xl">{getStepTypeIcon(step.type)}</span>
                          <span className="text-amber-800 font-semibold">{step.instruction}</span>
                        </div>
                        <div className="text-amber-600 text-sm">
                          Duración: {formatTime(step.duration)} • Tipo: {step.type}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => startPractice(currentRitual)}
                  className="bg-amber-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-amber-600 transition-colors flex items-center gap-3 text-lg"
                >
                  <Play className="w-6 h-6" />
                  Practicar Ritual Completo
                </button>
                
                <button
                  onClick={() => {/* TODO: Add audio playback */}}
                  className="bg-orange-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-orange-600 transition-colors flex items-center gap-3 text-lg"
                >
                  <Volume2 className="w-6 h-6" />
                  Escuchar Audio
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Practice Screen - Guided Hands-on Practice
  if (gameState === 'practice' && practiceMode && currentRitual) {
    const currentStepData = currentRitual.steps[currentStep];
    const progress = ((currentStep + 1) / currentRitual.steps.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-orange-800 to-yellow-700">
        <div className="container mx-auto px-6 py-8">
          {/* Practice Header */}
          <div className="flex items-center justify-between mb-8 text-white">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setGameState('learning')}
                className="p-2 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 transition-all"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-cinzel text-2xl font-bold">Práctica Guiada</h1>
                <p className="text-orange-200">{currentRitual.name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{formatTime(practiceTimer)}</div>
                <div className="text-orange-200 text-sm">Tiempo</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{currentStep + 1}/{currentRitual.steps.length}</div>
                <div className="text-orange-200 text-sm">Paso Actual</div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-orange-200 text-sm mb-2">
              <span>Progreso del Ritual</span>
              <span>{Math.round(progress)}% Completado</span>
            </div>
            <div className="progress-bar bg-orange-800">
              <div 
                className="progress-fill bg-gradient-to-r from-yellow-400 to-orange-400" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Current Step */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-2xl text-center">
              <div className="mb-8">
                <div className="text-8xl mb-6">{getStepTypeIcon(currentStepData.type)}</div>
                <h2 className="font-cinzel text-3xl font-bold text-amber-800 mb-4">
                  Paso {currentStep + 1}
                </h2>
                <p className="text-xl text-amber-700 leading-relaxed">
                  {currentStepData.instruction}
                </p>
              </div>

              {/* Step Timer */}
              <div className="mb-8 p-6 bg-amber-50 rounded-xl">
                <p className="text-amber-700 font-semibold mb-2">
                  Duración recomendada: {formatTime(currentStepData.duration)}
                </p>
                <p className="text-amber-600 text-sm">
                  Tómate tu tiempo para realizar este paso correctamente
                </p>
              </div>

              {/* Control Buttons */}
              <div className="flex justify-center gap-4 mb-8">
                <button
                  onClick={() => setIsTimerRunning(!isTimerRunning)}
                  className="bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors flex items-center gap-2"
                >
                  {isTimerRunning ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  {isTimerRunning ? 'Pausar' : 'Continuar'}
                </button>
                
                <button
                  onClick={resetPractice}
                  className="bg-gray-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-600 transition-colors flex items-center gap-2"
                >
                  <RotateCcw className="w-5 h-5" />
                  Reiniciar
                </button>
              </div>

              {/* Complete Step Button */}
              <div className="text-center">
                <button
                  onClick={completeStep}
                  className="bg-green-500 text-white px-12 py-4 rounded-2xl font-bold text-lg hover:bg-green-600 transition-colors flex items-center gap-3 mx-auto shadow-lg"
                >
                  <CheckCircle className="w-6 h-6" />
                  {currentStep === currentRitual.steps.length - 1 ? 'Completar Ritual' : 'Paso Completado'}
                </button>
                <p className="text-amber-600 text-sm mt-3">
                  Presiona cuando hayas completado este paso correctamente
                </p>
              </div>

              {/* Steps Overview */}
              <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-2">
                {currentRitual.steps.map((step, index) => (
                  <div 
                    key={index}
                    className={`p-3 rounded-xl text-center ${
                      completedSteps.includes(index) 
                        ? 'bg-green-100 text-green-800 border-2 border-green-300' 
                        : currentStep === index
                        ? 'bg-amber-100 text-amber-800 border-2 border-amber-300'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <div className="text-2xl mb-1">{getStepTypeIcon(step.type)}</div>
                    <div className="text-xs font-semibold">Paso {index + 1}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default RitualsGame;